/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {collectStatesFromProvider, AdnRouterHelperApi} from '../routing/state-utils';
import _ from 'lodash';
import {OBJECT_TYPE} from "../common/constants/object-type";
import {creativeListCommons} from "./creative-commons";
import {PERMISSIONS} from "../../components/auth/auth-constants";
import {getResourceItemTitleResolves} from '../base/default-item-title-view';
import {LibraryCreativeListContextComponent} from '../library-creative/library-creative-list-context.component';
import {CreativeBulkUploadPageComponent} from './creative-bulk-upload-page.component';
import {CreativeContextActionsComponent} from './creative-context-actions.component';
import {AdminResourceItemRoutePageComponent} from '../common/controller/admin-resource-item-route-page.component';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {stateTitleView} from '../common/directives/html/state-title.component';

const listTemplate = '<adn-creative-list-page [list]="list"></adn-creative-list-page>';

export function creativeStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  return collectStatesFromProvider(function register($stateProvider, adnRouterHelperProvider) {
let rh = adnRouterHelperProvider.$get().createRouterHelper('creatives.creative', CreativeResource, 'app.adv', 'app', listTemplate);
    
        let paramsExtras = {modelId: '', modelName: '', fromLineItem: false, fromCreativeSet: false};
        $stateProvider.state(rh.listPath(), rh.list({
          views: _.merge({}, {
            'context@app': {
              component: LibraryCreativeListContextComponent,
              bindings: {readOnly: 'listReadOnly'}
            }
          }),
          resolve: {
            readOnly: function() {
              const LocalUserPermissions = adnRouterHelper.services.localUserPermissions();

              return !LocalUserPermissions.hasAny(PERMISSIONS.manageAdvertising.apiKey) && !LocalUserPermissions.hasAny(PERMISSIONS.axAdvertiser.apiKey) && !LocalUserPermissions.hasAny(PERMISSIONS.creativeAgency.apiKey);
            },
            extension: function() {
              const LocalNetworkProfile = adnRouterHelper.services.localNetworkProfile();
              const $state = adnRouterHelper.services.state();

              return {
                func: function(ctrl, afterFill) {
                  creativeListCommons(ctrl, afterFill, LocalNetworkProfile, $state);
                }
              };
            },
            sectionParam: function() {
              return 'creatives';
            }
          }
        }))
          .state(rh.newPath('bulkupload'), rh.item({
            url: '/bulk-upload',
            params: {lineItemId: false, lineItemName: false, creativeSetId: false, creativeSetName: false},
            resolve: {
              model: function() {
                return null;
              },
              creativeType: function() {
                return 'CREATIVE';
              }
            },
            views: {
              'content@app': {
                component: CreativeBulkUploadPageComponent,
                bindings: {creativeType: 'creativeType'}
              },
              'title@app': stateTitleView()
            },
            data: {
              title: 'Creative Bulk Upload'
            }
          }))
          .state(rh.newItemPath(), rh.newItem(paramsExtras))
          .state(rh.itemPath(), rh.modelCommsItem({
            params: paramsExtras,
            resolve: _.merge({}, getResourceItemTitleResolves('Creative'), {
              titleVariant: function() {
                return 'creative';
              },
              creativeType: function() {
                return OBJECT_TYPE.Creative;
              },
              lineItemInfo: /*@ngInject*/ function($stateParams) {
                return {
                  id: $stateParams.modelId,
                  name: $stateParams.modelName,
                  fromLineItem: $stateParams.fromLineItem,
                  fromCreativeSet: $stateParams.fromCreativeSet,
                  advertiserId: $stateParams.advertiserId,
                  advertiserName: $stateParams.advertiserName
                };
              },
              creativeEditRouteKey: function() {
                return 'creative-edit';
              },
              routeEditVariant: function() {
                return 'edit';
              },
              creativeEditInputs: /*@ngInject*/ function(creativeType, lineItemInfo) {
                return {creativeType, lineItemInfo};
              },
              model: /*@ngInject*/ function($stateParams) {
                const LocalNetworkProfile = adnRouterHelper.services.localNetworkProfile();

                if ($stateParams.isNew) {
                  let networkDefaults = LocalNetworkProfile.getDefaults();
                  return adnRouterHelper.services.resource(CreativeResource).create({
                    name: $stateParams.modelName ? (networkDefaults.defaultCreativeName || '').replace("{lineItemName}", $stateParams.modelName).replace("{fileName}", '') : "",
                    lineItem: {id: $stateParams.fromLineItem ? $stateParams.modelId : ''},
                    creativeSet: {id: $stateParams.fromCreativeSet ? $stateParams.modelId : ''},
                    id: $stateParams.id
                  });
                }
                return adnRouterHelper.services.resource(CreativeResource).getHydrated($stateParams);
              }
            }),
            views: {
              'content@app': {
                component: AdminResourceItemRoutePageComponent,
                bindings: {
                  item: 'creativeEditRouteKey',
                  variant: 'routeEditVariant',
                  inputOverrides: 'creativeEditInputs'
                }
              },
              'context@app': {
                component: CreativeContextActionsComponent,
                bindings: {model: 'model', modelComms: 'modelComms', isNew: 'isNew', readOnly: 'readOnly'}
              },
              'title@app': rh.getResourceItemTitleView()
            }
          }));
  }, adnRouterHelper);
}
